interface ArrayToSeparateValuesColumn<T> {
    displayName?: string;
    fieldName: string;
    format?: (value: any, obj: T) => string;
}

interface ArrayToSeparateValuesOptions<T> {
    columns?: Array<ArrayToSeparateValuesColumn<T>>;
    includeHeaderRow?: boolean;
    separator?: string;
    /*
     * Only supporting quotes as text qualifiers for now (makes constructing regex simpler by not having to cater for
     * Regex-symbolic characters)
     */
    textQualifier?: "'" | '"';
}

function containsAnySubstr(text: string, substrs: string[]): boolean {
    for (const substr of substrs) {
        if (text.includes(substr)) {
            return true;
        }
    }

    return false;
}

export function objectsToCsv<T>(objects: T[], options?: ArrayToSeparateValuesOptions<T>): string {
    const distinct = <TValue>(value: TValue, index: number, self: TValue[]) => self.indexOf(value) === index;

    const guessColumns = () =>
        objects
            .map((obj: T) => Object.getOwnPropertyNames(obj))
            .reduce((prev, curr) => prev.concat(curr))
            .filter(distinct)
            .sort()
            .map(fieldName => ({ fieldName }));

    const optionsWithDefaults = {
        includeHeaderRow: true,
        separator: ',',
        textQualifier: '"',

        ...(options || {}),
    };

    if (!optionsWithDefaults.columns) {
        optionsWithDefaults.columns = guessColumns();
    }

    const specialCharacters = [optionsWithDefaults.separator, optionsWithDefaults.textQualifier, '\n'];
    const textQualifierRegEx = new RegExp(optionsWithDefaults.textQualifier, 'g');

    const valuesToLine = (values: any[]) => {
        const line = values
            .map(value => {
                let stringifiedValue: string;
                let needsWrapping: boolean;

                switch (typeof value) {
                    case 'number':
                    case 'boolean':
                        stringifiedValue = `${value}`;
                        needsWrapping = false;
                        break;

                    case 'object':
                        if (value === null) {
                            stringifiedValue = 'null';
                        } else if (value === undefined) {
                            stringifiedValue = 'undefined';
                        } else {
                            stringifiedValue = '[object]';
                        }
                        needsWrapping = false;
                        break;

                    default:
                        stringifiedValue = String(value);
                        needsWrapping = containsAnySubstr(stringifiedValue, specialCharacters);
                        break;
                }

                if (needsWrapping) {
                    /*
                     * Replace any single instances of textQualifier character with a double instance of it
                     * Most/decent CSV readers will recognise two quotes next to each other as a single quote within
                     * A column, rather than the end of the column
                     */
                    const { textQualifier } = optionsWithDefaults;
                    stringifiedValue = stringifiedValue.replace(textQualifierRegEx, textQualifier + textQualifier);
                    stringifiedValue = `${textQualifier}${stringifiedValue}${textQualifier}`;
                }

                return stringifiedValue;
            })
            .join(optionsWithDefaults.separator);

        return line;
    };

    const objectToLine = (obj: T) => {
        const values = optionsWithDefaults.columns!.map(column => {
            let value = (obj as any)[column.fieldName];
            value = column.format ? column.format(value, obj) : value;
            return value;
        });
        const line = valuesToLine(values);
        return line;
    };

    const lines = objects.map(obj => objectToLine(obj));

    if (optionsWithDefaults.includeHeaderRow) {
        const headerLine = valuesToLine(optionsWithDefaults.columns.map(col => col.displayName || col.fieldName));
        lines.unshift(headerLine);
    }

    const output = lines.join('\n');

    return output;
}
